import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { useGetList, useNotify, useStore } from 'react-admin';
import {
    Box,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    AntennaId,
    AntennaSummary,
    SupplyLight,
    SupplyId,
} from '@wwf/sdk';
import { getSupplyInventory, updateSupplyInventory } from '@wwf/sdk';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { SupplyLightCard } from '@app/supply/show/SupplyLightCard';
import { InventoryByAntennaFilterToolbar } from '@app/inventory/InventoryByAntennaFilterToolbar';
import {
    SupplyInventoryEditCellDialog,
    type SupplyEditCellState,
    type SupplyInventorySaveValues,
} from '@app/supply/inventory/SupplyInventoryEditCellDialog';
import {
    applySupplyInventoryFilters,
    EMPTY_SUPPLY_INVENTORY_FILTERS,
    hasActiveSupplyInventoryFilters,
    type SupplyInventoryFilters,
} from '@app/supply/inventory/supplyInventoryFilters';

const INVENTORY_QUERY_KEY = ['supply_inventory_per_antenna'] as const;
const COMPRESSED_VIEW_STORE_KEY = 'supply.inventory.compressedView';
const FILTERS_STORE_KEY = 'supply.inventory.filters';
const COMPRESSED_SUPPLY_COLUMN_WIDTH = 180;
const COMPRESSED_ANTENNA_COLUMN_WIDTH = 88;

function inventoryCellKey(antennaId: AntennaId, supplyId: SupplyId) {
    return `${antennaId}\0${supplyId}`;
}

export const SupplyInventoryByAntenna = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    const { data: antennas, isPending: antennasPending } =
        useGetList<AntennaSummary>('antenna', {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'name', order: 'ASC' },
        });

    const { data: supplies, isPending: suppliesPending } =
        useGetList<SupplyLight>('supply', {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'name', order: 'ASC' },
            filter: { enabled: true },
        });

    const {
        data: inventoryRows,
        isPending: inventoryPending,
        isError: inventoryError,
    } = useQuery({
        queryKey: INVENTORY_QUERY_KEY,
        queryFn: () => getSupplyInventory(),
    });

    const cellByKey = useMemo(() => {
        const m = new Map<
            string,
            { count: number; inventoryComment: string | null }
        >();
        for (const row of inventoryRows ?? []) {
            m.set(inventoryCellKey(row.antennaId, row.supplyId), {
                count: row.inventoryCount ?? 0,
                inventoryComment: row.inventoryComment,
            });
        }
        return m;
    }, [inventoryRows]);

    const upsertMutation = useMutation({
        mutationFn: async (payload: {
            antennaId: AntennaId;
            supply_id: SupplyId;
            count: number;
            inventoryComment: string | null;
        }) => {
            await updateSupplyInventory(payload.supply_id, payload.antennaId, {
                inventoryCount: payload.count,
                inventoryComment: payload.inventoryComment,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: INVENTORY_QUERY_KEY,
            });
            notify('Inventory updated', { type: 'success' });
        },
        onError: (err: Error) => {
            notify(err.message ?? 'Update failed', { type: 'error' });
        },
    });

    const [editCell, setEditCell] = useState<SupplyEditCellState | null>(null);
    const [compressedView, setCompressedView] = useStore<boolean>(
        COMPRESSED_VIEW_STORE_KEY,
        false
    );
    const [filters, setFilters] = useStore<SupplyInventoryFilters>(
        FILTERS_STORE_KEY,
        EMPTY_SUPPLY_INVENTORY_FILTERS
    );

    const getInventoryCount = useCallback(
        (supplyId: SupplyId, antennaId: AntennaId) =>
            cellByKey.get(inventoryCellKey(antennaId, supplyId))?.count ?? 0,
        [cellByKey]
    );

    const handleCompressedViewChange = useCallback(
        (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setCompressedView(checked);
        },
        [setCompressedView]
    );

    const openEdit = useCallback((params: SupplyEditCellState) => {
        setEditCell({
            antennaId: params.antennaId,
            supplyId: params.supplyId,
            antennaName: params.antennaName,
            supplyName: params.supplyName,
            count: params.count,
            inventoryComment: params.inventoryComment,
        });
    }, []);

    const closeEdit = useCallback(() => {
        setEditCell(null);
    }, []);

    const handleSaveEdit = useCallback(
        (values: SupplyInventorySaveValues) => {
            if (!editCell) return;
            upsertMutation.mutate(
                {
                    antennaId: editCell.antennaId,
                    supply_id: editCell.supplyId,
                    count: values.count,
                    inventoryComment: values.inventoryComment,
                },
                { onSuccess: closeEdit }
            );
        },
        [closeEdit, editCell, upsertMutation]
    );

    const supplyTypes = useMemo(
        () =>
            [
                ...new Set(
                    (supplies ?? [])
                        .map(supply => supply.type)
                        .filter((type): type is string => Boolean(type))
                ),
            ].sort((a, b) => a.localeCompare(b)),
        [supplies]
    );

    const { supplies: filteredSupplyList, antennas: filteredAntennaList } =
        useMemo(
            () =>
                applySupplyInventoryFilters({
                    supplies: supplies ?? [],
                    antennas: antennas ?? [],
                    filters,
                    getInventoryCount,
                }),
            [antennas, filters, getInventoryCount, supplies]
        );

    const handleClearFilters = useCallback(() => {
        setFilters(EMPTY_SUPPLY_INVENTORY_FILTERS);
    }, [setFilters]);

    if (antennasPending || suppliesPending || inventoryPending) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (inventoryError) {
        return (
            <Typography color="error">
                Could not load inventory. Try again or check your permissions.
            </Typography>
        );
    }

    const antennaList = antennas ?? [];
    const supplyList = supplies ?? [];

    return (
        <Box sx={{ pt: 2 }}>
            <InventoryByAntennaFilterToolbar
                rowSearchLabel="Supplies"
                rowSearchPlaceholder="Search name, description, type..."
                filters={filters}
                onFiltersChange={baseFilters =>
                    setFilters({ ...filters, ...baseFilters })
                }
                hasActiveFilters={hasActiveSupplyInventoryFilters(filters)}
                onClearFilters={handleClearFilters}
                extraFilters={
                    <>
                        <TextField
                            select
                            size="small"
                            label="Supply type"
                            value={filters.supplyType}
                            onChange={event =>
                                setFilters({
                                    ...filters,
                                    supplyType: event.target.value,
                                })
                            }
                            sx={{ minWidth: 180, flex: '0 1 180px' }}
                        >
                            <MenuItem value="">All types</MenuItem>
                            {supplyTypes.map(type => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={filters.notForSale}
                                    onChange={(_event, checked) =>
                                        setFilters({
                                            ...filters,
                                            notForSale: checked,
                                        })
                                    }
                                />
                            }
                            label="Not for sale"
                            sx={{ ml: 0.5, flexShrink: 0 }}
                        />
                    </>
                }
            />

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                sx={{ mb: 2 }}
            >
                <Typography variant="body2" color="text.secondary">
                    Stock count per supply (rows) and antenna (columns). Click a
                    cell to edit the count and optional comment.
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={compressedView}
                            onChange={handleCompressedViewChange}
                        />
                    }
                    label="Compressed view"
                    sx={{ flexShrink: 0, mt: 1 }}
                />
            </Box>

            {!supplyList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No enabled supplies to display.
                </Typography>
            ) : !antennaList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No antennas to display.
                </Typography>
            ) : !filteredSupplyList.length || !filteredAntennaList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No supplies or antennas match the current filters.
                </Typography>
            ) : (
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        maxHeight: '80dvh',
                        width: '80dvw',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={
                            compressedView
                                ? {
                                      tableLayout: 'fixed',
                                      minWidth:
                                          COMPRESSED_SUPPLY_COLUMN_WIDTH +
                                          filteredAntennaList.length *
                                              COMPRESSED_ANTENNA_COLUMN_WIDTH,
                                  }
                                : undefined
                        }
                    >
                        {compressedView && (
                            <colgroup>
                                <col
                                    style={{
                                        width: COMPRESSED_SUPPLY_COLUMN_WIDTH,
                                    }}
                                />
                                {filteredAntennaList.map(antenna => (
                                    <col
                                        key={antenna.id}
                                        style={{
                                            width: COMPRESSED_ANTENNA_COLUMN_WIDTH,
                                        }}
                                    />
                                ))}
                            </colgroup>
                        )}
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        top: 0,
                                        zIndex: 5,
                                        bgcolor: 'background.paper',
                                        minWidth: compressedView
                                            ? COMPRESSED_SUPPLY_COLUMN_WIDTH
                                            : 220,
                                        width: compressedView
                                            ? COMPRESSED_SUPPLY_COLUMN_WIDTH
                                            : undefined,
                                        maxWidth: compressedView
                                            ? COMPRESSED_SUPPLY_COLUMN_WIDTH
                                            : undefined,
                                        boxShadow: theme =>
                                            `1px 0 0 0 ${theme.palette.divider}`,
                                    }}
                                >
                                    Supply
                                </TableCell>
                                {filteredAntennaList.map(antenna => (
                                    <TableCell
                                        key={antenna.id}
                                        align="center"
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 3,
                                            bgcolor: 'background.paper',
                                            minWidth: compressedView
                                                ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                : 120,
                                            width: compressedView
                                                ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                : undefined,
                                            maxWidth: compressedView
                                                ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                : 160,
                                            overflow: 'hidden',
                                            whiteSpace: compressedView
                                                ? 'nowrap'
                                                : 'normal',
                                            verticalAlign: 'bottom',
                                            px: compressedView
                                                ? 0.75
                                                : undefined,
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            justifyContent="flex-start"
                                            width="100%"
                                            overflow="hidden"
                                        >
                                            <AntennaLightCard
                                                record={antenna}
                                            />
                                        </Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredSupplyList.map(supply => (
                                <TableRow key={supply.id} hover>
                                    <TableCell
                                        sx={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            bgcolor: 'background.paper',
                                            fontWeight: 500,
                                            width: compressedView
                                                ? COMPRESSED_SUPPLY_COLUMN_WIDTH
                                                : undefined,
                                            maxWidth: compressedView
                                                ? COMPRESSED_SUPPLY_COLUMN_WIDTH
                                                : undefined,
                                            overflow: 'hidden',
                                            boxShadow: theme =>
                                                `1px 0 0 0 ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box overflow="hidden">
                                            <SupplyLightCard
                                                record={supply}
                                                compressed={compressedView}
                                            />
                                        </Box>
                                    </TableCell>
                                    {filteredAntennaList.map(antenna => {
                                        const cell = cellByKey.get(
                                            inventoryCellKey(
                                                antenna.id,
                                                supply.id
                                            )
                                        ) ?? {
                                            count: 0,
                                            inventoryComment: null,
                                        };
                                        const { count, inventoryComment } =
                                            cell;
                                        const commentPreview =
                                            inventoryComment &&
                                            inventoryComment.length > 40
                                                ? `${inventoryComment.slice(0, 40)}…`
                                                : inventoryComment;
                                        return (
                                            <TableCell
                                                key={`${supply.id}-${antenna.id}`}
                                                align="center"
                                                onClick={() =>
                                                    openEdit({
                                                        antennaId: antenna.id,
                                                        supplyId: supply.id,
                                                        antennaName:
                                                            antenna.name,
                                                        supplyName:
                                                            supply.name ??
                                                            'Supply',
                                                        count,
                                                        inventoryComment:
                                                            inventoryComment,
                                                    })
                                                }
                                                sx={{
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    verticalAlign: 'middle',
                                                    width: compressedView
                                                        ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                        : undefined,
                                                    maxWidth: compressedView
                                                        ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                        : 160,
                                                    px: compressedView
                                                        ? 0.75
                                                        : undefined,
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                }}
                                                title={
                                                    inventoryComment ||
                                                    undefined
                                                }
                                                aria-label={`Count for ${supply.name} at ${antenna.name}: ${count}${inventoryComment ? `. Comment: ${inventoryComment}` : ''}. Click to edit.`}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                        py: 0.25,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {count}
                                                    </Typography>
                                                    {commentPreview ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                lineHeight: 1.2,
                                                                display:
                                                                    '-webkit-box',
                                                                WebkitLineClamp:
                                                                    compressedView
                                                                        ? 1
                                                                        : 2,
                                                                WebkitBoxOrient:
                                                                    'vertical',
                                                                overflow:
                                                                    'hidden',
                                                                wordBreak:
                                                                    'break-word',
                                                            }}
                                                        >
                                                            {commentPreview}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <SupplyInventoryEditCellDialog
                cell={editCell}
                onClose={closeEdit}
                onSave={handleSaveEdit}
                isSavePending={upsertMutation.isPending}
            />
        </Box>
    );
};
